"use client"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"
import { useFormStatus } from "react-dom"
import Spinner from "../utils/Spinner"

// Color constants
const COLOR_PRIMARY = 'bg-nfPurple-400'
const COLOR_PRIMARY_HOVER = 'hover:bg-nfPurple-500'
const COLOR_SECONDARY = 'bg-nfPurple-700'
const COLOR_SECONDARY_HOVER = 'hover:bg-nfPurple-800'
const COLOR_TEXT = 'text-nfBlue-100'
const COLOR_BORDER = 'border-nfPurple-600'
const COLOR_FOCUS = 'focus:ring-nfBlue-200'
const COLOR_DESTRUCTIVE = 'bg-red-600/20'
const COLOR_DESTRUCTIVE_HOVER = 'hover:bg-red-500'
const RING_OFFSET = 'ring-offset-nfPurple-900'

const buttonVariants = cva(
  `active:brightness-110 inline-flex items-center justify-center whitespace-nowrap rounded-md text-md font-medium ${RING_OFFSET} transition-colors focus-visible:outline-none focus-visible:ring-2 ${COLOR_FOCUS} focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50`,
  {
    variants: {
      variant: {
        default: `${COLOR_PRIMARY} ${COLOR_TEXT} ${COLOR_PRIMARY_HOVER}`,
        destructive:
          `${COLOR_DESTRUCTIVE} text-white ${COLOR_DESTRUCTIVE_HOVER} focus:ring-red-600 border border-red-600`,
        outline:
          `border ${COLOR_BORDER} ${COLOR_SECONDARY_HOVER} hover:${COLOR_TEXT}`,
        secondary:
          `${COLOR_SECONDARY} ${COLOR_TEXT} ${COLOR_SECONDARY_HOVER}`,
        ghost: `hover:${COLOR_SECONDARY} hover:${COLOR_TEXT}`,
        link: `text-nfBlue-500 underline-offset-4 hover:underline`,
        none: ""
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
      colorScheme: {
        "none": "",
        "pink": "bg-nfPurple-400",
        "green": "bg-lime-500/20 border-lime-500 hover:bg-lime-600",
        "orange": "bg-orange-500/20 border-orange-500 hover:bg-orange-600",
        "violet": "bg-pink-500/20 border-pink-500 hover:bg-pink-600",
        "purple": "bg-nfPurple-600/30",
        "blue": "bg-nfBlue-500/20 border-nfBlue-500 hover:bg-nfBlue-600"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      colorScheme: "none"
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, colorScheme, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const { pending } = useFormStatus();
    if ((props.type == "submit" || props.formAction) && pending) {
      props.children = <Spinner />
      props.disabled = true;
    }

    return (
      <Comp
        className={ cn(buttonVariants({ variant, size, colorScheme, className })) }
        ref={ ref }
        { ...props }
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

